import { NumberFormatter } from '@internationalized/number';
import { CURRENCY_COOKIE, LOCALE_COOKIE } from '~/constants/cookies';
import type { Locale } from '~/i18n/i18n-config';
import { i18n } from '~/i18n/i18n-config';
import { getCookie } from '~/utils/cookies';

export type CurrencyName = 'AUD' | 'CAD' | 'CHF' | 'DKK' | 'EUR' | 'GBP' | 'NOK' | 'NZD' | 'SEK' | 'USD';

export type CurrencyCookie = CurrencyName | undefined;

export const CURRENCIES: CurrencyName[] = ['GBP', 'AUD', 'CAD', 'CHF', 'DKK', 'EUR', 'NOK', 'NZD', 'SEK', 'USD'];

export const isValidCurrency = (currency: string): currency is CurrencyName => {
  return (CURRENCIES as string[]).includes(currency);
};

/** Derives the currency symbol from Intl.NumberFormat (e.g. 'GBP' → '£', 'EUR' → '€') */
export const getCurrencySymbol = (currency: CurrencyName): string => {
  return (
    new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency
  );
};

export const getDisplayCurrencyCookie = () => {
  // TODO proper type checking of cookie
  return getCookie(CURRENCY_COOKIE) as CurrencyCookie;
};

export const createCurrencyFormatter = (locale: Locale, currency: CurrencyName, showDecimals = true) => {
  return new NumberFormatter(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
    useGrouping: true,
    currencyDisplay: 'symbol'
  });
};

export const formatGBP = (amount: number, _showDecimals = false) => {
  const formatter = createCurrencyFormatter(i18n.defaultLocale, 'GBP');
  return formatter.format(amount);
};

export const getUserLocale = (): Locale => (getCookie(LOCALE_COOKIE) as Locale) || i18n.defaultLocale;

export const formatFixedAmount = (
  locale: Locale,
  currency: CurrencyName,
  amount: number,
  showDecimals = false
): string => createCurrencyFormatter(locale, currency, showDecimals).format(amount);

export const parseNumericAmount = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null) return 0;
  const str = typeof value === 'number' ? String(value) : value;
  return Number.parseFloat(str.replace(/[^0-9.]/g, '')) || 0;
};
